import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material'
import {
  Delete,
  Warning,
  Cancel,
} from '@mui/icons-material'
import {
  Auction,
  PickupLocation,
  ShopType,
  RentalCompanyLocation,
  VendorLocation,
  TowCompanyLocation,
} from '@otw/models'

// Extend PickupLocation to include shop type for repair shops
interface RepairShopLocation extends PickupLocation {
  shop_type?: ShopType[]
  name?: string
}

export type DeleteLocationKind =
  | 'pickup'
  | 'repair_shop'
  | 'rental_company'
  | 'vendor'
  | 'tow_company'
  | 'auction'

interface DeleteDialogProps {
  open: boolean
  location: PickupLocation | RepairShopLocation | RentalCompanyLocation | VendorLocation | TowCompanyLocation | Auction | null
  locationKind: DeleteLocationKind
  onClose: () => void
  onConfirm: () => void
  isDeleting: boolean
}

const DeleteDialog: React.FC<DeleteDialogProps> = ({
  open,
  location,
  locationKind,
  onClose,
  onConfirm,
  isDeleting,
}) => {
  const getLocationType = () => {
    switch (locationKind) {
      case 'repair_shop':
        return 'Repair Shop'
      case 'rental_company':
        return 'Rental Company'
      case 'vendor':
        return 'Vendor'
      case 'tow_company':
        return 'Tow Company'
      case 'auction':
        return 'Auction'
      default:
        return 'Pickup Location'
    }
  }

  const label = getLocationType()

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Warning sx={{ color: 'warning.main' }} />
        Delete {label}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Are you sure you want to delete this {label.toLowerCase()}?
          </Typography>
          {location && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {locationKind === 'auction' && (
                <Typography variant="body2" fontWeight="medium">
                  {(location as Auction).name}
                </Typography>
              )}
              {locationKind === 'vendor' && (
                <Typography variant="body2" fontWeight="medium">
                  {(location as VendorLocation).name}
                </Typography>
              )}
              {locationKind === 'tow_company' && (
                <Typography variant="body2" fontWeight="medium">
                  {(location as TowCompanyLocation).name}
                </Typography>
              )}
              {locationKind === 'rental_company' && (
                <Typography variant="body2" fontWeight="medium">
                  {(location as RentalCompanyLocation).business_name}
                </Typography>
              )}
              {locationKind !== 'auction' && (
                <>
                  <Typography variant="body2" fontWeight="medium">
                    {(location as PickupLocation | RepairShopLocation | RentalCompanyLocation | VendorLocation | TowCompanyLocation).address}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {(location as PickupLocation | RepairShopLocation | RentalCompanyLocation | VendorLocation | TowCompanyLocation).city},{' '}
                    {(location as PickupLocation | RepairShopLocation | RentalCompanyLocation | VendorLocation | TowCompanyLocation).state}{' '}
                    {(location as PickupLocation | RepairShopLocation | RentalCompanyLocation | VendorLocation | TowCompanyLocation).zip}
                  </Typography>
                  {(location as PickupLocation).contact_name && (
                    <Typography variant="body2" color="text.secondary">
                      Contact: {(location as PickupLocation).contact_name}
                    </Typography>
                  )}
                </>
              )}
              {locationKind === 'repair_shop' &&
                'shop_type' in location &&
                location.shop_type &&
                Array.isArray(location.shop_type) &&
                location.shop_type.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  Type: {location.shop_type.map((type: ShopType) => {
                    const labels: Record<ShopType, string> = {
                      mechanic_shop: 'Mechanic Shop',
                      body_shop: 'Body Shop',
                      parts: 'Parts',
                      other: 'Other',
                      mobile: 'Mobile',
                    }
                    return labels[type]
                  }).join(', ')}
                </Typography>
              )}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={isDeleting}
          startIcon={<Cancel />}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          color="error"
          variant="contained"
          disabled={isDeleting}
          startIcon={<Delete />}
        >
          {isDeleting ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DeleteDialog
